import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonButton from "components/ArgonButton";
import { useState, useEffect } from "react";
import { Grid, Chip, Divider } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import api from "../../../utils/api";

function ViewDemo() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestedTutor, setRequestedTutor] = useState(null);
  const [requestedTutorLoading, setRequestedTutorLoading] = useState(false);

  useEffect(() => {
    fetchDemoDetails();
  }, [id]);

  const fetchSlotDetails = async (demoData) => {
    try {
      if (demoData.meetingDetails?.preferredTimeSlots?.length > 0) {
        const slotPromises = demoData.meetingDetails.preferredTimeSlots.map(async (slotId) => {
          try {
            const response = await api.get(`/slots/${slotId}`);
            return response.data.success ? response.data.data : null;
          } catch (error) {
            console.error(`Error fetching slot ${slotId}:`, error);
            return null;
          }
        });

        const slotDetails = await Promise.all(slotPromises);
        const validSlots = slotDetails.filter((slot) => slot !== null);

        // Replace slot IDs with slot objects
        if (validSlots.length > 0) {
          demoData.meetingDetails.preferredTimeSlots = validSlots;
        }
      }
    } catch (error) {
      console.error("Error fetching slot details:", error);
    }
  };

  const fetchDemoDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/demo/${id}`);
      console.log("Demo API response:", response.data);

      if (response.data.success && response.data.data) {
        // Handle different response structures
        const demoData = response.data.data.demo || response.data.data;
        console.log("Demo data:", demoData);
        console.log("Tutor info:", demoData.tutorByAdmin);
        console.log("Student info:", demoData.tutorByStudent);
        console.log("Meeting details:", demoData.meetingDetails);
        console.log("Preferred time slots:", demoData.meetingDetails?.preferredTimeSlots);

        // If preferredTimeSlots has IDs, fetch slot details
        if (demoData.meetingDetails?.preferredTimeSlots?.length > 0) {
          const firstSlot = demoData.meetingDetails.preferredTimeSlots[0];
          if (typeof firstSlot === "string") {
            // IDs detected, fetch slot details
            await fetchSlotDetails(demoData);
          }
        }

        // Hydrate requested tutor (front-end only) if meetingDetails.tutorId is present
        try {
          const maybeTutor = demoData.meetingDetails?.tutorId;
          if (maybeTutor) {
            // If object already populated, use it directly
            if (typeof maybeTutor === "object" && (maybeTutor.fullName || maybeTutor.name)) {
              setRequestedTutor(maybeTutor);
            } else if (typeof maybeTutor === "string") {
              setRequestedTutorLoading(true);
              // Fetch tutor by id using existing API shape from EditDemo
              const tRes = await api.get(`/tutor`, { params: { id: maybeTutor } });
              const tData = tRes?.data?.data;
              if (tData && (tData._id || tData.id)) {
                setRequestedTutor(tData);
              } else {
                setRequestedTutor(null);
              }
            }
          } else {
            setRequestedTutor(null);
          }
        } catch (e) {
          console.error("Error hydrating requested tutor:", e);
          setRequestedTutor(null);
        } finally {
          setRequestedTutorLoading(false);
        }

        setDemo(demoData);
      } else {
        console.error("Demo not found or API error:", response.data);
        setDemo(null);
      }
    } catch (error) {
      console.error("Error fetching demo details:", error);
      setDemo(null);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "REQUESTED":
        return "warning";
      case "SCHEDULED":
        return "info";
      case "DONE":
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      case "REJECTED":
        return "error";
      case "RESCHEDULED":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <Card>
            <ArgonBox p={3} textAlign="center">
              <ArgonTypography variant="h6" color="text" fontWeight="medium">
                Loading demo details...
              </ArgonTypography>
              <ArgonTypography variant="body2" color="secondary" mt={1}>
                Please wait while we fetch the demo information.
              </ArgonTypography>
            </ArgonBox>
          </Card>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!demo) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3}>
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              Demo not found
            </ArgonTypography>
            <ArgonButton
              variant="gradient"
              color="info"
              onClick={() => navigate("/demos")}
              sx={{ mt: 2 }}
            >
              Back to Demos
            </ArgonButton>
          </ArgonBox>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                <ArgonBox display="flex" alignItems="center" gap={2}>
                  <ArgonButton
                    variant="text"
                    color="secondary"
                    size="small"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate("/demos")}
                  >
                    Back to Demos
                  </ArgonButton>
                  <ArgonTypography variant="h6" fontWeight="medium">
                    Demo Details
                  </ArgonTypography>
                  <ArgonBadge
                    variant="gradient"
                    badgeContent={demo.demoStatus || demo.status || "REQUESTED"}
                    color={getStatusColor(demo.demoStatus || demo.status)}
                    size="sm"
                  />
                </ArgonBox>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/demos/edit/${id}`)}
                >
                  Edit Demo
                </ArgonButton>
              </ArgonBox>

              <Grid container spacing={3}>
                {/* Student Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <ArgonBox p={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
                        <PersonIcon color="primary" />
                        <ArgonTypography variant="h6" fontWeight="medium">
                          Student Information
                        </ArgonTypography>
                      </ArgonBox>
                      <Divider sx={{ mb: 2 }} />

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Name:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {demo.tutorByStudent?.name ||
                            demo.tutorByStudent?.fullName ||
                            demo.studentId?.name ||
                            demo.student?.name ||
                            demo.studentName ||
                            "N/A"}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Phone:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {demo.tutorByStudent?.phone ||
                            demo.studentId?.parentId?.phone ||
                            demo.student?.parentId?.phone ||
                            demo.studentPhone ||
                            "N/A"}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Email:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {demo.tutorByStudent?.email ||
                            demo.studentId?.parentId?.email ||
                            demo.student?.parentId?.email ||
                            demo.studentEmail ||
                            "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Assigned Tutor (Admin) */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <ArgonBox p={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
                        <SchoolIcon color="primary" />
                        <ArgonTypography variant="h6" fontWeight="medium">
                          Assigned Tutor (Admin)
                        </ArgonTypography>
                      </ArgonBox>
                      <Divider sx={{ mb: 2 }} />

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Name:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {demo.tutorByAdmin?.fullName ||
                            demo.tutorId?.fullName ||
                            demo.tutor?.fullName ||
                            demo.tutorName ||
                            "Not Assigned"}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Email:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {demo.tutorByAdmin?.email ||
                            demo.tutorId?.email ||
                            demo.tutor?.email ||
                            demo.tutorEmail ||
                            "N/A"}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Requested Tutor (Student's Request) */}
                <Grid item xs={12}>
                  <Card>
                    <ArgonBox p={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
                        <SchoolIcon color="primary" />
                        <ArgonTypography variant="h6" fontWeight="medium">
                          Requested Tutor
                        </ArgonTypography>
                        {demo.meetingDetails?.tutorId && (
                          <ArgonBadge
                            variant="gradient"
                            badgeContent="Request based"
                            color="info"
                            size="xs"
                            container
                            sx={{ ml: 1 }}
                          />
                        )}
                      </ArgonBox>
                      <Divider sx={{ mb: 2 }} />

                      {demo.meetingDetails?.tutorId ? (
                        requestedTutorLoading ? (
                          <ArgonTypography variant="body2" color="secondary">
                            Loading requested tutor details...
                          </ArgonTypography>
                        ) : (
                          <>
                            <ArgonBox mb={2}>
                              <ArgonTypography variant="button" fontWeight="bold" color="text">
                                Name:
                              </ArgonTypography>
                              <ArgonTypography variant="body2" color="text" ml={1}>
                                {requestedTutor?.fullName ||
                                  requestedTutor?.name ||
                                  demo.meetingDetails?.tutorId?.fullName ||
                                  demo.meetingDetails?.tutorId?.name ||
                                  "N/A"}
                              </ArgonTypography>
                            </ArgonBox>

                            <ArgonBox mb={2}>
                              <ArgonTypography variant="button" fontWeight="bold" color="text">
                                Phone:
                              </ArgonTypography>
                              <ArgonTypography variant="body2" color="text" ml={1}>
                                {requestedTutor?.phone ||
                                  demo.meetingDetails?.tutorId?.phone ||
                                  "N/A"}
                              </ArgonTypography>
                            </ArgonBox>

                            <ArgonBox mb={2}>
                              <ArgonTypography variant="button" fontWeight="bold" color="text">
                                Email:
                              </ArgonTypography>
                              <ArgonTypography variant="body2" color="text" ml={1}>
                                {requestedTutor?.email ||
                                  demo.meetingDetails?.tutorId?.email ||
                                  "N/A"}
                              </ArgonTypography>
                            </ArgonBox>
                          </>
                        )
                      ) : (
                        <ArgonTypography variant="body2" color="text">
                          No tutor requested by student.
                        </ArgonTypography>
                      )}
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Demo Schedule Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <ArgonBox p={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
                        <CalendarTodayIcon color="primary" />
                        <ArgonTypography variant="h6" fontWeight="medium">
                          Schedule Information
                        </ArgonTypography>
                      </ArgonBox>
                      <Divider sx={{ mb: 2 }} />

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Date:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {formatDate(demo.preferredDate || demo.date)}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Time Slots:
                        </ArgonTypography>
                        <ArgonBox display="flex" flexWrap="wrap" gap={1} mt={1}>
                          {(demo.preferredTimeSlots || demo.meetingDetails?.preferredTimeSlots)
                            ?.length > 0 ? (
                            (demo.preferredTimeSlots || demo.meetingDetails.preferredTimeSlots).map(
                              (slot, index) => (
                                <Chip
                                  key={slot._id || index}
                                  label={
                                    typeof slot === "string"
                                      ? `Slot ID: ${slot}`
                                      : `${slot.startTime || "N/A"} - ${slot.endTime || "N/A"}`
                                  }
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              )
                            )
                          ) : (
                            <ArgonTypography variant="body2" color="text">
                              No time slots specified
                            </ArgonTypography>
                          )}
                        </ArgonBox>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Demo Type:
                        </ArgonTypography>
                        <Chip
                          label={demo.demoType || "Online"}
                          size="small"
                          variant="outlined"
                          color={demo.demoType === "OFFLINE" ? "secondary" : "primary"}
                          sx={{ ml: 1 }}
                        />
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>

                {/* Additional Information */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: "100%" }}>
                    <ArgonBox p={2}>
                      <ArgonBox display="flex" alignItems="center" gap={1} mb={2}>
                        <LocationOnIcon color="primary" />
                        <ArgonTypography variant="h6" fontWeight="medium">
                          Additional Information
                        </ArgonTypography>
                      </ArgonBox>
                      <Divider sx={{ mb: 2 }} />

                      {demo.demoType === "OFFLINE" && demo.address && (
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="button" fontWeight="bold" color="text">
                            Address:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" color="text" ml={1}>
                            {demo.address}
                          </ArgonTypography>
                        </ArgonBox>
                      )}

                      {(demo.notes || demo.meetingDetails?.notes) && (
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="button" fontWeight="bold" color="text">
                            Notes:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" color="text" ml={1}>
                            {demo.notes || demo.meetingDetails?.notes}
                          </ArgonTypography>
                        </ArgonBox>
                      )}

                      {demo.rejectReason && (
                        <ArgonBox mb={2}>
                          <ArgonTypography variant="button" fontWeight="bold" color="error">
                            Reject Reason:
                          </ArgonTypography>
                          <ArgonTypography variant="body2" color="error" ml={1}>
                            {demo.rejectReason}
                          </ArgonTypography>
                        </ArgonBox>
                      )}

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Created:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {formatDate(demo.createdAt)}
                        </ArgonTypography>
                      </ArgonBox>

                      <ArgonBox mb={2}>
                        <ArgonTypography variant="button" fontWeight="bold" color="text">
                          Last Updated:
                        </ArgonTypography>
                        <ArgonTypography variant="body2" color="text" ml={1}>
                          {formatDate(demo.updatedAt)}
                        </ArgonTypography>
                      </ArgonBox>
                    </ArgonBox>
                  </Card>
                </Grid>
              </Grid>
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default ViewDemo;
